import React from 'react';
import { motion } from 'framer-motion';
import { Clock, PoundSterling, ShieldCheck, CreditCard } from 'lucide-react';
export function RevolutionSection() {
  return <section className="bg-gold py-24 md:py-32 relative z-20 overflow-hidden" data-id="element-133">
      <div className="container mx-auto px-6 md:px-12" data-id="element-134">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8
      }} className="text-center mb-20 md:mb-28" data-id="element-135">
          <h2 className="text-primary leading-[0.9]" data-id="element-136">
            <span className="font-serif italic font-light text-4xl md:text-6xl lg:text-7xl block mb-4" data-id="element-137">
              The
            </span>
            <span className="font-heading font-bold uppercase text-5xl md:text-7xl lg:text-8xl tracking-tight block" data-id="element-138">
              Housekeeping
            </span>
            <span className="font-heading font-bold uppercase text-5xl md:text-7xl lg:text-8xl tracking-tight block" data-id="element-139">
              Revolution
            </span>
            <span className="font-script text-5xl md:text-7xl lg:text-8xl block mt-4 -rotate-2" data-id="element-140">
              is here
            </span>
          </h2>
        </motion.div>

        {/* Features + Images Grid — matching design layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12" data-id="element-141">
          {/* Left Column */}
          <div className="space-y-12" data-id="element-142">
            {/* Feature 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8
          }} className="flex flex-col gap-4" data-id="element-143">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center" data-id="element-144">
                <Clock className="w-7 h-7 text-primary" data-id="element-145" />
              </div>
              <h3 className="text-3xl md:text-4xl font-heading font-bold text-primary uppercase leading-tight tracking-wide" data-id="element-146">
                Book us anytime,
                <br data-id="element-147" />
                from anywhere
              </h3>
              <p className="text-primary/70 text-lg leading-relaxed max-w-md" data-id="element-148">
                Book a 100% online, no-hassle clean. Choose your service, date,
                and time that works best for you.
              </p>
            </motion.div>

            {/* Feature 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8,
            delay: 0.1
          }} className="flex flex-col gap-4" data-id="element-149">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center" data-id="element-150">
                <PoundSterling className="w-7 h-7 text-primary" data-id="element-151" />
              </div>
              <h3 className="text-3xl md:text-4xl font-heading font-bold text-primary uppercase leading-tight tracking-wide" data-id="element-152">
                Personalized
                <br data-id="element-153" />
                pricing online
              </h3>
              <p className="text-primary/70 text-lg leading-relaxed max-w-md" data-id="element-154">
                Get an instant quote. No hidden fees or surprises. You only pay
                for what you need.
              </p>
            </motion.div>

            {/* Image — Kitchen */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8
          }} className="h-[300px] md:h-[350px] rounded-2xl overflow-hidden shadow-2xl" data-id="element-155">
              <img src="https://cdn.magicpatterns.com/uploads/hHpFJHcwRaZ4MLEjPmwVhE/white-sideboard-living-room-interior-with-copy-space-edit.png" alt="White sideboard in clean living room" className="w-full h-full object-cover" data-id="element-156" />
            </motion.div>
          </div>

          {/* Right Column */}
          <div className="space-y-12" data-id="element-157">
            {/* Image — Yellow Chair Room */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="h-[300px] md:h-[350px] rounded-2xl overflow-hidden shadow-2xl" data-id="element-158">
              <img src="https://cdn.magicpatterns.com/uploads/gHZJnEjDVVvZ17CeC3XiQV/yellow-armchair-living-room-with-copy-space.jpg" alt="Yellow armchair in living room" className="w-full h-full object-cover" data-id="element-159" />
            </motion.div>

            {/* Feature 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8,
            delay: 0.1
          }} className="flex flex-col gap-4" data-id="element-160">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center" data-id="element-161">
                <ShieldCheck className="w-7 h-7 text-primary" data-id="element-162" />
              </div>
              <h3 className="text-3xl md:text-4xl font-heading font-bold text-primary uppercase leading-tight tracking-wide" data-id="element-163">
                Safe, Insured,
                <br data-id="element-164" />
                and Vetted
              </h3>
              <p className="text-primary/70 text-lg leading-relaxed max-w-md" data-id="element-165">
                Our professionals are rigorously vetted, background checked, and
                fully insured for your peace of mind.
              </p>
            </motion.div>

            {/* Feature 4 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="flex flex-col gap-4" data-id="element-166">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center" data-id="element-167">
                <CreditCard className="w-7 h-7 text-primary" data-id="element-168" />
              </div>
              <h3 className="text-3xl md:text-4xl font-heading font-bold text-primary uppercase leading-tight tracking-wide" data-id="element-169">
                Secure, cash-free
                <br data-id="element-170" />
                payment
              </h3>
              <p className="text-primary/70 text-lg leading-relaxed max-w-md" data-id="element-171">
                Pay securely online. No need to worry about having cash on hand.
                We accept all major credit cards.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}